import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { ExpiryChip } from '@l2u/ui/components/ExpiryChip.tsx';
import { fromDateInput } from '@l2u/ui/lib/expiry.ts';
import type { SubmissionFile } from '@l2u/ui/lib/types.ts';
import { showExpiryButton } from '../lib/expiryButton.ts';
import { invalidateCompany } from '../lib/invalidateCompany.ts';
import { ExpiryEditor } from './ExpiryEditor.tsx';

// A live file's expiry chip plus the admin's "Edit expiry" / "Add expiry"
// control (PATCH .../files/:fileId). Shared by the questionnaire rows
// (SubmissionFileItem) and the Compliance Documents tab (DocumentReviewTable),
// so both surfaces save and refresh the same way.
export function FileExpiryControl({
  companyId,
  submissionId,
  file,
  requiresExpiry,
  editable,
}: {
  companyId: string;
  submissionId: string;
  file: SubmissionFile;
  /** Mirrors the server rule: hides Clear on requires_expiry questions. */
  requiresExpiry: boolean;
  /** False for read-only roles and for frozen approved rows' historical files. */
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();
  const expiryMutation = useMutation({
    mutationFn: (expires_at: string | null) =>
      adminApi.patch<{ file: SubmissionFile }>(
        `/api/companies/${companyId}/submissions/${submissionId}/files/${file.id}`,
        { expires_at },
      ),
    onSuccess: () => {
      setEditing(false);
      // The row's chip re-renders from the submissions query; the route also
      // re-runs the company status rollup, so the vendor header badge and the
      // dashboard/vendors list (both ['companies'], 30s staleTime) can go
      // stale. Expiring Documents must reflect the new date without a reload.
      // invalidateCompany covers the submissions list and the file history.
      invalidateCompany(qc, companyId);
      void qc.invalidateQueries({ queryKey: ['companies'] });
      void qc.invalidateQueries({ queryKey: ['expiring-documents'] });
    },
  });

  if (editing) {
    return (
      <>
        <ExpiryEditor
          initialExpiresAt={file.expires_at}
          requiresExpiry={requiresExpiry}
          saving={expiryMutation.isPending}
          onSave={(dateInput) => expiryMutation.mutate(fromDateInput(dateInput))}
          onClear={() => expiryMutation.mutate(null)}
          onCancel={() => {
            setEditing(false);
            expiryMutation.reset();
          }}
        />
        {expiryMutation.error != null && <ErrorMessage error={expiryMutation.error} />}
      </>
    );
  }

  return (
    <>
      <ExpiryChip expiresAt={file.expires_at} />
      {showExpiryButton({
        expiryEditable: editable,
        requiresExpiry,
        expiresAt: file.expires_at,
      }) && (
        <button
          type="button"
          className="text-xs text-slate-400 hover:text-slate-600 hover:underline"
          aria-label={`Edit expiry date for ${file.file_name}`}
          onClick={() => setEditing(true)}
        >
          {file.expires_at ? 'Edit expiry' : 'Add expiry'}
        </button>
      )}
    </>
  );
}
